import {Box, Button, ButtonGroup, Stack} from "@mui/material";
import {
	DataTable,
	DataTableColumnSettings,
	SpringPageable,
	useDataTableColumnStorage,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React, {useEffect, useMemo, useState} from "react";
import {useAsync} from "react-async-hook";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import {InvoiceSummary, InvoiceType, useInvoicesApi} from "../../api";
import {Column, ExportButton} from "../../component/ExportButton";
import {useUser} from "../../context/auth";
import {useLocalization} from "../../i18n";
import {notBlank} from "../../util";
import {useInvoiceColumns} from "./columns";
import {InvoiceFilterBar, useInvoiceFilter} from "./filter";
import {SubmitInvoiceButton} from "./submit-invoice-button";
import {useInvoiceNavigator} from "./useInvoiceNavigator";
import {BulkRouteButton} from "./BulkRouteButton";
import { BulkAuditButton } from "./BulkAuditButton";

export function useInvoicePage() {
	return useDataTableStorage("InvoiceList", {
		defaults: { sortField: "id", sortDirection: "desc" },
		storageType: "session",
	});
}

export function InvoiceList() {
	const {s, t} = useLocalization();
	const {listInvoices} = useInvoicesApi();
	const user = useUser();
	const [filter, setFilter] = useInvoiceFilter();
	const storage = useInvoicePage();
	const pageable = useSpringPageable(storage);

	const {setNavigator} = useInvoiceNavigator();

	const [searchParams, setSearchParams] = useSearchParams();

	const viewParam = searchParams.get("view");
	const typeParam = searchParams.get("invoiceType");
	const resetParam = searchParams.get("reset");

	const [selected, setSelected] = useState<InvoiceSummary[]>([]);

	useEffect(() => {
		if (notBlank(viewParam)) {
			// If reset parameter is present, clear the filter first
			const baseFilter = resetParam === "true" ? {} : filter;

			switch (viewParam) {
				case "Mine":
					setFilter({...baseFilter, submittedOrOwnedBy: user.id});
					break;
				case "Audit":
					setFilter({...baseFilter, auditRequestedFrom: user.email});
					break;
				case "AwaitingRouting":
					setFilter({...baseFilter, awaitingRouting: true});
					break;
				case "AwaitingAudit":
					setFilter({...baseFilter, awaitingAudit: true});
					break;
				case "All":
					setFilter(baseFilter);
					break;
			}

			// Remove the reset parameter from URL after processing
			if (resetParam === "true") {
				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete("reset");
				setSearchParams(newSearchParams, {replace: true});
			}
		}
	}, [viewParam, resetParam]);

	useEffect(() => {
		if (notBlank(typeParam)) {
			setFilter({
				invoiceType: typeParam as InvoiceType,
			});
		}
	}, [typeParam]);

	const query = useMemo(() => ({...filter, ...pageable}), [filter, pageable]);
	const {result, loading, error, execute} = useAsync(listInvoices, [query]);

	const refresh = () => execute(query);

	useEffect(() => {
		if (result) {
			const {number, content, last} = result;
			setNavigator({page: number ?? 0, ids: content?.map(i => i.id) ?? [], last: last ?? false});
		}
	}, [result]);

	const {rows, page} = useSpringPage(result);

	const available = useInvoiceColumns();
	const {columns, setColumns} = useDataTableColumnStorage("InvoiceListColumns", available);

	return (
		<View maxWidth="xl">
			<ViewHeader
				title={t("invoices.plural")}
				actions={
					<ButtonGroup variant="outlined" disableElevation>
						<Button
							component={RouterLink}
							to={`/invoices/export?total=${result?.totalElements ?? 0}`}
							color="inherit"
						>
							{t("invoices.excelExport.button")}
						</Button>
						{/* <ExportInvoicesButton /> */}
						<SubmitInvoiceButton />
					</ButtonGroup>
				}
			/>
			<Stack direction="row" spacing={2} alignItems="center">
				<Box flex={1}>
					<InvoiceFilterBar
						value={filter}
						onChange={setFilter}
					/>
				</Box>
				<Box>
					<DataTableColumnSettings
						columns={available}
						selected={columns}
						onChange={setColumns}
						labels={s("columnSettings")}
					/>
				</Box>
			</Stack>
			<DataTable
				columns={columns}
				selected={selected}
				onSelectedChange={setSelected}
				rows={rows}
				page={page}
				loading={loading}
				error={error}
				{...storage}
				toolbar={
					<Box px={2} py={1}>
						<ButtonGroup>
							<BulkRouteButton invoices={selected} onRouted={refresh} />
							<BulkAuditButton invoices={selected} onAudited={refresh}/>
						</ButtonGroup>

					</Box>
				}
			/>
		</View>
	);
}

function ExportInvoicesButton() {
	const {t} = useLocalization();
	const {listInvoices} = useInvoicesApi();
	const [filter] = useInvoiceFilter();

	async function fetch(pageable: SpringPageable) {
		return await listInvoices({...pageable, ...filter});
	}

	return <ExportButton onFetch={fetch} columns={EXPORT_COLUMNS} name={t("invoices.plural")} />;
}

const EXPORT_COLUMNS: Column<InvoiceSummary>[] = [
	{name: "Web-ID", value: i => i.id, width: 20, format: "n"},
	{name: "Original-Dateiname", value: i => i.originalFilename},
	{name: "Einreicher", value: i => i.user.displayName, width: 20},
	{name: "Eingereicht", value: i => i.submitted, width: 20, format: "d"},
	{name: "Rechnungstyp", value: i => i.invoiceType, width: 20},
	{name: "Status", value: i => i.invoiceState, width: 20},
	{name: "Brutto-Betrag", value: i => i.gross, width: 20, format: "n"},
	{name: "Währung", value: i => i.currency, width: 20},
	{name: "Lieferant", value: i => i.creditor?.companyName, width: 20},
	{name: "Empfänger", value: i => i.inbox.name, width: 20},
];
